<template>
  <Card class="transaction-card cursor-pointer" @click="handleCardClick">
    <template #content>
      <!-- Mobile layout: stacked -->
      <div class="flex flex-col gap-3 px-2 py-2 md:hidden">
        <div class="flex items-center justify-between">
          <div class="font-medium text-lg">{{ getPartyName() }}</div>
          <div class="text-lg font-semibold" :class="getAmountClass()">
            {{ formatCurrency(transaction.total_amount) }}
          </div>
        </div>
        <div class="flex items-center justify-between">
          <div class="text-sm text-gray-500">{{ formatDate(transaction.transaction_date) }}</div>
          <div class="text-sm text-gray-500">{{ transaction.items?.length || 0 }} items</div>
        </div>
        <div class="flex items-center justify-between">
          <div class="flex items-center gap-2">
            <Tag
              :value="transaction.transaction_type === 'purchase' ? 'Purchase' : 'Sale'"
              :severity="transaction.transaction_type === 'purchase' ? 'info' : 'success'"
            />
            <Tag
              :value="getStatusLabel(transaction.transaction_status || transaction.payment_status)"
              :severity="
                getStatusSeverity(transaction.transaction_status || transaction.payment_status)
              "
            />
          </div>
          <Button
            icon="pi pi-ellipsis-v"
            size="small"
            text
            rounded
            @click.stop="toggleMenu"
            aria-haspopup="true"
            aria-controls="action_menu"
          />
        </div>
        <Menu ref="menu" id="action_menu" :model="actionItems" :popup="true" />
      </div>

      <!-- Desktop layout: table row -->
      <div class="hidden md:flex items-center px-2 py-2 gap-6">
        <!-- Date -->
        <div class="text-sm text-gray-500 min-w-[100px]">
          {{ formatDate(transaction.transaction_date) }}
        </div>

        <!-- Type Tag -->
        <div class="min-w-[80px]">
          <Tag
            :value="transaction.transaction_type === 'purchase' ? 'Purchase' : 'Sale'"
            :severity="transaction.transaction_type === 'purchase' ? 'info' : 'success'"
          />
        </div>

        <!-- Name -->
        <div class="font-medium flex-1 min-w-[200px]">
          {{ getPartyName() }}
        </div>

        <!-- Amount -->
        <div class="text-lg font-semibold min-w-[100px] text-right" :class="getAmountClass()">
          {{ formatCurrency(transaction.total_amount) }}
        </div>

        <!-- Item Count -->
        <div class="text-sm text-gray-500 min-w-[80px] text-right">
          {{ transaction.items?.length || 0 }} items
        </div>

        <!-- Status Tag -->
        <div class="min-w-[100px]">
          <Tag
            :value="getStatusLabel(transaction.transaction_status || transaction.payment_status)"
            :severity="
              getStatusSeverity(transaction.transaction_status || transaction.payment_status)
            "
          />
        </div>

        <!-- Actions -->
        <div class="flex gap-2 min-w-[120px]">
          <Button
            icon="pi pi-eye"
            size="small"
            outlined
            v-tooltip="'View'"
            @click.stop="$emit('view', transaction)"
          />
          <Button
            icon="pi pi-pencil"
            size="small"
            outlined
            v-tooltip="'Edit'"
            @click.stop="$emit('edit', transaction)"
          />
          <Button
            icon="pi pi-trash"
            size="small"
            severity="danger"
            outlined
            v-tooltip="'Delete'"
            @click.stop="$emit('delete', transaction)"
          />
        </div>
      </div>
    </template>
  </Card>
</template>

<script setup lang="ts">
import { computed, ref } from 'vue'
import { TransactionModel } from '@/models/TransactionModel'
import type { TransactionRecord } from '@/types/Transaction'

// Components
import Card from 'primevue/card'
import Tag from 'primevue/tag'
import Button from 'primevue/button'
import Menu from 'primevue/menu'

interface Props {
  transaction: TransactionRecord
}

interface Emits {
  (e: 'view', transaction: TransactionRecord): void
  (e: 'edit', transaction: TransactionRecord): void
  (e: 'delete', transaction: TransactionRecord): void
}

const props = defineProps<Props>()
const emit = defineEmits<Emits>()

const menu = ref()

const model = computed(() => new TransactionModel(props.transaction))

const actionItems = computed(() => [
  {
    label: 'View',
    icon: 'pi pi-eye',
    command: () => emit('view', props.transaction),
  },
  {
    label: 'Edit',
    icon: 'pi pi-pencil',
    command: () => emit('edit', props.transaction),
  },
  {
    label: 'Delete',
    icon: 'pi pi-trash',
    command: () => emit('delete', props.transaction),
  },
])

function handleCardClick() {
  emit('view', props.transaction)
}

function toggleMenu(event: Event) {
  menu.value.toggle(event)
}

function getPartyName(): string {
  return model.value.party.name
}

function getAmountClass(): string {
  return props.transaction.transaction_type === 'sale' ? 'text-green-500' : 'text-blue-500'
}

function getStatusLabel(status: string): string {
  return TransactionModel.getStatusLabel(status)
}

function getStatusSeverity(status: string): string {
  switch (status) {
    case 'completed':
      return 'success'
    case 'pending':
      return 'warning'
    case 'cancelled':
      return 'danger'
    case 'editing':
      return 'info'
    default:
      return 'secondary'
  }
}

function formatCurrency(amount: number): string {
  return TransactionModel.formatCurrency(amount)
}

function formatDate(date: Date): string {
  return TransactionModel.formatDate(date)
}
</script>

<style scoped>
.transaction-card {
  transition:
    transform 0.2s ease,
    box-shadow 0.2s ease;
}

.transaction-card:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
}
</style>
